/**
 * 전역 검색 입력 (X2 — Knowledge Autocomplete + W5 Command palette)
 * H3 GlobalSearchContext와 연동 — Cmd+K는 GlobalSearchModal · TopBar 클릭은 inline palette.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AutoComplete, Input, Spin, Tag, Typography } from 'antd';
import type { InputRef } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ResolvedCommand } from '@shared/business-workbench/command-resolver';
import {
  COMMAND_PALETTE_GROUP_LABELS,
  COMMAND_PALETTE_GROUP_ORDER,
  type PaletteGroupedCommands,
} from '@shared/business-workbench/workbench-command-palette-contract';
import { css } from '@shared/utils/omitUndefined';
import { createBackendApiClient, type GlobalSearchItem } from '../../services/backendApiClient';
import { getListPathForBusinessDomain } from '../../utils/domainPathUtils';
import { useGlobalSearch } from '../../contexts/GlobalSearchContext';
import { useWorkbenchCommandContext } from '../../hooks/workbench/useWorkbenchCommandContext';
import { executeWorkbenchCommand } from '../../utils/globalSearchCommands';
import { resolvePaletteKernelDeps } from '../../workbench/workbench-palette-command-bridge';
import { PaletteIntentProposalPanel } from './PaletteIntentProposalPanel';
import styles from './GlobalSearchInput.module.css';

const { Text } = Typography;

const DOMAIN_LABEL_MAP: Record<string, string> = {
  sellable_item: '상품',
  service_item: '서비스',
  external_entity: '외부 관계',
  internal_entity: '내부 조직',
  user_entity: '사용자',
  asset_item: '자산',
  knowledge_report: '보고서',
  calendar_anchor: '일정',
  virtual_space: '공간',
  physical_space: '물리 공간',
  assignment_execution: '담당 배정',
  knowledge_plan: '계획',
  knowledge_policy: '정책',
  consumable_item: '소모품',
  raw_material_item: '원자재',
  flow: 'Flow',
  flow_event: 'Flow',
  finance_flow: '재무 흐름',
  finance_state: '재무 상태',
};

interface EntitySearchOption {
  label: React.ReactNode;
  value: string;
  item: GlobalSearchItem;
}

interface CommandSearchOption {
  label: React.ReactNode;
  value: string;
  command: ResolvedCommand;
}

type SearchOption = EntitySearchOption | CommandSearchOption;

interface GroupedOption {
  label: React.ReactNode;
  options: SearchOption[];
}

export interface GlobalSearchInputProps {
  tenantId: string;
  onSelect?: (item: GlobalSearchItem) => void;
  className?: string;
}

function getDomainLabel(domain: string): string {
  return DOMAIN_LABEL_MAP[domain] ?? domain;
}

function CommandFocusContext({ businessDomain, label }: { businessDomain: string; label: string }) {
  return (
    <div className={css(styles.focusContext)} data-testid="workbench-command-focus">
      <Tag>{getDomainLabel(businessDomain)}</Tag>
      <Text type="secondary" className={css(styles.focusLabel)}>
        {label}
      </Text>
    </div>
  );
}

function renderCommandOptionLabel(command: ResolvedCommand): React.ReactNode {
  if (command.disabled && command.reason) {
    return (
      <span className={css(styles.commandDisabled)}>
        {command.label}{' '}
        <Text type="secondary" className={css(styles.commandReason)}>
          — {command.reason}
        </Text>
      </span>
    );
  }
  return command.label;
}

function groupResultsToOptions(items: GlobalSearchItem[]): GroupedOption[] {
  const groups = new Map<string, GlobalSearchItem[]>();
  for (const item of items) {
    const bucket = groups.get(item.businessDomain) ?? [];
    bucket.push(item);
    groups.set(item.businessDomain, bucket);
  }
  return [...groups.entries()].map(([domain, domainItems]) => ({
    label: <b>{getDomainLabel(domain)}</b>,
    options: domainItems.map((item) => ({
      label: `${item.displayName || item.name} [${getDomainLabel(domain)}]`,
      value: item.id,
      item,
    })),
  }));
}

function buildCommandGroupedOptions(grouped: PaletteGroupedCommands): GroupedOption[] {
  const groups: GroupedOption[] = [];
  for (const group of COMMAND_PALETTE_GROUP_ORDER) {
    const commands = grouped[group];
    if (commands.length === 0) {
      continue;
    }
    groups.push({
      label: <b>{COMMAND_PALETTE_GROUP_LABELS[group]}</b>,
      options: commands.map((command) => ({
        label: renderCommandOptionLabel(command),
        value: `cmd:${command.id}`,
        command,
        disabled: command.disabled,
      })),
    });
  }
  return groups;
}

function getDetailPathForItem(item: GlobalSearchItem): string {
  const listPath = getListPathForBusinessDomain(item.businessDomain);
  if (listPath !== '/entity') {
    return `${listPath}/${item.id}`;
  }
  return listPath;
}

function isCommandOption(option: SearchOption | GroupedOption): option is CommandSearchOption {
  return 'command' in option;
}

export const GlobalSearchInput: React.FC<GlobalSearchInputProps> = ({
  tenantId,
  onSelect,
  className,
}) => {
  const navigate = useNavigate();
  const { registerSearchInputFocus } = useGlobalSearch();
  const inputRef = useRef<InputRef>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [query, setQuery] = useState('');
  const [knowledgeOptions, setKnowledgeOptions] = useState<GroupedOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const {
    grouped,
    hasObjectContext,
    rootLabel,
    businessDomain: focusBusinessDomain,
    intentProposal,
    commandContext,
    commandObject,
  } = useWorkbenchCommandContext({ query, tenantId });
  const commandOptions = useMemo(() => buildCommandGroupedOptions(grouped), [grouped]);
  const paletteKernelDeps = useMemo(
    () => resolvePaletteKernelDeps(tenantId, navigate),
    [navigate, tenantId]
  );
  const options = useMemo(
    () => [...commandOptions, ...knowledgeOptions],
    [commandOptions, knowledgeOptions]
  );

  const apiClient = useMemo(() => createBackendApiClient(tenantId), [tenantId]);

  const focusInput = useCallback(() => {
    inputRef.current?.focus({ cursor: 'all' });
    setOpen(true);
  }, []);

  useEffect(() => {
    registerSearchInputFocus(focusInput);
  }, [registerSearchInputFocus, focusInput]);

  useEffect(
    () => () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    },
    []
  );

  const resetSearch = useCallback(() => {
    setOpen(false);
    setQuery('');
    setKnowledgeOptions([]);
  }, []);

  const handleNavigate = useCallback(
    (item: GlobalSearchItem) => {
      resetSearch();
      navigate(getDetailPathForItem(item));
    },
    [navigate, resetSearch]
  );

  const handleCommandSelect = useCallback(
    (command: ResolvedCommand) => {
      if (command.disabled) {
        return;
      }
      resetSearch();
      const kernelDeps = resolvePaletteKernelDeps(tenantId, navigate);
      executeWorkbenchCommand(command, navigate, {
        ...(kernelDeps ? { kernelDeps } : {}),
      });
    },
    [navigate, resetSearch, tenantId]
  );

  const handleSearch = useCallback(
    (value: string) => {
      setQuery(value);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(async () => {
        const trimmedQuery = value.trim();
        if (!trimmedQuery) {
          setKnowledgeOptions([]);
          return;
        }
        setLoading(true);
        try {
          const result = await apiClient.searchGlobal(trimmedQuery, 10);
          setKnowledgeOptions(groupResultsToOptions(result.items));
        } catch {
          setKnowledgeOptions([]);
        } finally {
          setLoading(false);
        }
      }, 300);
    },
    [apiClient]
  );

  const handleSelect = useCallback(
    (_value: string, option: SearchOption | GroupedOption) => {
      if (!('options' in option)) {
        if (isCommandOption(option)) {
          handleCommandSelect(option.command);
          return;
        }
        onSelect?.(option.item);
        handleNavigate(option.item);
      }
    },
    [onSelect, handleNavigate, handleCommandSelect]
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      resetSearch();
    }
  };

  const hasResults = options.length > 0;
  const notFoundContent =
    query.length > 0 && !loading && !hasResults
      ? '결과 없음'
      : query.length === 0 && !hasResults
        ? '검색어를 입력하거나 명령을 선택하세요'
        : null;

  const focusChip =
    hasObjectContext && rootLabel && focusBusinessDomain ? (
      <CommandFocusContext businessDomain={focusBusinessDomain} label={rootLabel} />
    ) : null;

  return (
    <div {...(className ? { className } : {})} data-testid="global-search-input-wrap">
      <AutoComplete
        options={options}
        onSearch={handleSearch}
        onSelect={handleSelect}
        open={open}
        onDropdownVisibleChange={setOpen}
        value={query}
        notFoundContent={notFoundContent}
        dropdownRender={(menu) => (
          <div data-testid="workbench-command-palette-dropdown">
            {focusChip}
            {intentProposal ? (
              <PaletteIntentProposalPanel
                tenantId={tenantId}
                proposal={intentProposal}
                query={query.trim()}
                commandContext={commandContext}
                navigate={navigate}
                {...(focusBusinessDomain !== undefined
                  ? { businessDomain: focusBusinessDomain }
                  : {})}
                {...(commandObject?.id !== undefined ? { objectId: commandObject.id } : {})}
                executeOptions={{
                  ...(paletteKernelDeps ? { kernelDeps: paletteKernelDeps } : {}),
                }}
              />
            ) : null}
            {menu}
          </div>
        )}
      >
        <Input
          ref={inputRef}
          prefix={<SearchOutlined />}
          suffix={loading ? <Spin size="small" /> : null}
          placeholder="전체 검색 (Cmd+K)"
          allowClear
          onKeyDown={handleKeyDown}
          aria-label="전역 검색"
        />
      </AutoComplete>
    </div>
  );
};

export default GlobalSearchInput;
